import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  AnalyticsEventName,
  AnalyticsPageType,
  getClientBrowserParameters,
  sendShopifyAnalytics,
  ShopifySalesChannel,
  useShopifyCookies,
} from "@shopify/hydrogen-react";
import type { CurrencyCode, LanguageCode } from "@shopify/hydrogen-react/storefront-api-types";

import Config, { type Instance } from "../../config";
import { register as registerAddToCart, type AddToCartEvent } from "../../events/addToCart";
import { register as registerPageView, type PageViewEvent } from "../../events/pageView";
import { register as registerViewProduct, type ViewProductEvent } from "../../events/viewProduct";

// Map Prismic page types to Shopify Analytics page types
const mapPageType = (prismicType: string): string => {
  const mapping: Record<string, string> = {
    // Home
    page_home: AnalyticsPageType.home,

    // Cart
    page_cart: AnalyticsPageType.cart,

    // Blog/Articles
    page_article_overview: AnalyticsPageType.blog,
    page_article: AnalyticsPageType.article,

    // Products & Shop
    product: AnalyticsPageType.product,
    page_shop_category: AnalyticsPageType.collection,

    // 404
    page_404: AnalyticsPageType.notFound,

    // Policy pages (AGB, TCS, Datenschutz)
    page_static: AnalyticsPageType.policy,

    // All other pages → page
    page_shop: AnalyticsPageType.page,
    page_content: AnalyticsPageType.page,
    page_help_overview: AnalyticsPageType.page,
    page_help_category: AnalyticsPageType.page,
    page_faq_question: AnalyticsPageType.page,
    treatment: AnalyticsPageType.page,
    page_treatments_overview: AnalyticsPageType.page,
    treatment_addon: AnalyticsPageType.page,
    page_treatment_addons_overview: AnalyticsPageType.page,
    location: AnalyticsPageType.page,
    page_locations_overview: AnalyticsPageType.page,
    page_location_partner: AnalyticsPageType.page,
    page_dentist: AnalyticsPageType.page,
    marketing_treatment: AnalyticsPageType.page,
  };

  return mapping[prismicType] || AnalyticsPageType.page;
};

// Map i18n language codes to Shopify LanguageCode
const mapLanguageCode = (lang: string): LanguageCode => {
  const mapping: Record<string, LanguageCode> = {
    de: "DE",
    en: "EN",
    fr: "FR",
    pt: "PT",
    cn: "ZH",
    pl: "PL",
  };
  return (mapping[lang.toLowerCase()] || "EN") as LanguageCode;
};

// Map shop instance to currency code for PAGE_VIEW events.
// For product/cart events, we use the currency from Shopify directly.
const mapCurrency = (instance: Instance): CurrencyCode => {
  const mapping: Record<Instance, CurrencyCode> = {
    CH: "CHF",
    EU: "EUR",
    BR: "BRL",
    CN: "CNY",
  };
  return mapping[instance];
};

const ShopifyAnalytics: React.FC = () => {
  const { i18n } = useTranslation();

  const { domain: shopDomain, shopId, storefrontId } = Config().shop.shopifyClient;
  const currency = mapCurrency(Config().shop.instance);

  const track = (eventName: string, payload: Parameters<typeof sendShopifyAnalytics>[0]["payload"]) => {
    console.info(`🎯 → 🛍️ → %c${eventName}`, "font-style: italic", payload);
    sendShopifyAnalytics({ eventName, payload }, shopDomain).catch((error) => {
      console.error(`🚨 → 🎯 → 🛍️ → %c${eventName} failed`, "font-style: italic", error);
    });
  };

  useShopifyCookies({
    hasUserConsent: Boolean(shopId),
    checkoutDomain: shopDomain,
  });

  // Base payload shared by all events
  const getBasePayload = () => {
    const browserParams = getClientBrowserParameters();
    return {
      hasUserConsent: true,
      shopId,
      currency,
      ...browserParams,
      canonicalUrl: browserParams.url,
      shopifySalesChannel: ShopifySalesChannel.hydrogen,
      storefrontId,
      acceptedLanguage: mapLanguageCode(i18n.language),
      analyticsAllowed: true,
      marketingAllowed: true,
      gdprEnforced: false,
    };
  };

  // Register event listeners
  useEffect(() => {
    if (!shopId) return;

    const cleanupFns: Array<() => void> = [];

    // PAGE_VIEW (non-product pages)
    cleanupFns.push(
      registerPageView((e: CustomEvent<PageViewEvent>) => {
        const { category } = e.detail;
        if (category === "product") return; // Handled by viewProduct

        track(AnalyticsEventName.PAGE_VIEW, {
          ...getBasePayload(),
          pageType: mapPageType(category),
        });
      }),
    );

    // PAGE_VIEW (product pages with product data)
    cleanupFns.push(
      registerViewProduct((e: CustomEvent<ViewProductEvent>) => {
        const { productGid, variantGid, name, variantName, brand, category, value, sku } = e.detail;

        track(AnalyticsEventName.PAGE_VIEW, {
          ...getBasePayload(),
          pageType: AnalyticsPageType.product,
          totalValue: value,
          products: [
            {
              productGid,
              variantGid: variantGid || undefined,
              name,
              variantName: variantName || undefined,
              brand,
              category,
              price: String(value),
              sku: sku || undefined,
              quantity: 1,
            },
          ],
        });
      }),
    );

    // ADD_TO_CART
    cleanupFns.push(
      registerAddToCart((e: CustomEvent<AddToCartEvent>) => {
        const { cartId, items, value } = e.detail;

        track(AnalyticsEventName.ADD_TO_CART, {
          ...getBasePayload(),
          cartId,
          totalValue: value,
          products: items.map((item) => ({
            productGid: item.productGid,
            variantGid: item.variantGid || undefined,
            name: item.name,
            variantName: item.variantName || undefined,
            brand: "ALPINE WHITE",
            category: item.category,
            price: String(item.price),
            quantity: item.quantity,
          })),
        });
      }),
    );

    return () => {
      cleanupFns.forEach((cleanup) => cleanup?.());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config values are stable
  }, []);

  return null;
};

export default ShopifyAnalytics;
